// Settings → Team & Permissions. The account owner (and co-admins) add people,
// decide what each may see and do, keep reusable templates, and watch activity.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Users, ShieldCheck, BookmarkCheck, Activity } from 'lucide-react';
import { teamApi } from '../../services/account';
import { Notice } from './ui';
import MembersTab from './MembersTab';
import TemplatesTab from './TemplatesTab';
import ActivityTab from './ActivityTab';
import AuthorityDrawer from './AuthorityDrawer';
import InviteMemberModal from './InviteMemberModal';

const TABS = [
  { key: 'members', label: 'Members', icon: Users },
  { key: 'templates', label: 'Templates', icon: BookmarkCheck },
  { key: 'activity', label: 'Activity', icon: Activity },
];

const TeamPage = () => {
  const [tab, setTab] = useState('members');
  const [options, setOptions] = useState(null);
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [drawer, setDrawer] = useState(null); // { mode, member?, memberIds?, template? }
  const [activityMember, setActivityMember] = useState(null);

  const flashTimer = useRef(null);
  const flash = useCallback((text) => {
    setMessage(text);
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setMessage(''), 4000);
  }, []);
  useEffect(() => () => window.clearTimeout(flashTimer.current), []);

  const reload = useCallback(async () => {
    try {
      const [opts, members, tpls] = await Promise.all([teamApi.options(), teamApi.members(), teamApi.templates()]);
      setOptions(opts);
      setData(members);
      setTemplates(tpls);
      setLoadError('');
    } catch (e) {
      setLoadError(e.message);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const myRole = data?.myRole || options?.myRole;

  const onDrawerSaved = async (text, { keepOpen = false } = {}) => {
    flash(text);
    await reload();
    if (!keepOpen) setDrawer(null);
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/40">
            <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Team & Permissions</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">
          Give people their own login to this account and choose exactly what each of them can see and do.
          Everything they add counts against your plan; billing stays with you.
        </p>

        <div className="flex gap-1 border-b border-gray-200 dark:border-slate-700 mb-5">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === key
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        <div className="space-y-3 mb-4">
          {loadError && <Notice>{loadError}</Notice>}
          {message && <Notice tone="green">{message}</Notice>}
        </div>

        {!data && !loadError && <p className="text-sm text-gray-500">Loading…</p>}

        {data && tab === 'members' && (
          <MembersTab
            data={data}
            options={options}
            myRole={myRole}
            onReload={reload}
            flash={flash}
            onInvite={() => setInviteOpen(true)}
            onAuthority={(m) => setDrawer({ mode: 'member', member: m })}
            onBulk={(ids) => setDrawer({ mode: 'bulk', memberIds: ids })}
            onViewActivity={(id) => { setActivityMember(id); setTab('activity'); }}
          />
        )}
        {data && tab === 'templates' && (
          <TemplatesTab
            options={options}
            templates={templates}
            onReload={reload}
            flash={flash}
            onEdit={(t) => setDrawer({ mode: 'template', template: t })}
          />
        )}
        {data && tab === 'activity' && (
          <ActivityTab data={data} memberFilter={activityMember} setMemberFilter={setActivityMember} />
        )}
      </div>

      <InviteMemberModal
        open={inviteOpen}
        options={options}
        myRole={myRole}
        onInvited={reload}
        onClose={(next) => {
          setInviteOpen(false);
          if (next && next.openAuthorityFor) {
            setDrawer({ mode: 'member', member: { id: next.openAuthorityFor, name: next.name, email: next.email, role: 'member' } });
          }
        }}
      />

      <AuthorityDrawer
        open={Boolean(drawer)}
        mode={drawer?.mode}
        member={drawer?.member}
        memberIds={drawer?.memberIds}
        template={drawer?.template}
        options={options}
        onClose={() => setDrawer(null)}
        onSaved={onDrawerSaved}
      />
    </div>
  );
};

export default TeamPage;
